import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import type { DatasetRequest, DatasetResponse } from '@@/packages/shared/libs/search/types';
import type { ClientNetworkConfig } from '@@/packages/shared/types/models/clientNetwork';
import type { Connection } from '@@/packages/shared/types/models/connection';
import type { OrganisationOwnershipStatus } from '@@/packages/shared/types/models/organisation';
import { calculateNthDegree, calculateMaxNthDegree, getConcentrationRiskOrgIDs } from '../utils';
import {
  CLIENT_NETWORK_CONFIG_QUERY,
  CLIENT_NETWORK_LIST_QUERY,
  CLIENT_NETWORK_QUERY,
  COMMUNITY_CONCENTRATION_RISK_STATS_QUERY,
  COMMUNITY_NETWORK_QUERY,
} from './queryKeys';

export type NetworkConnection = {
  id: string;
  clientID: string;
  supplierID: string;
  degree: number;
  // We only return 'status' for client-networks, NOT federated or community
  status?: Connection['status'];
};

export type NetworkOrganisation = {
  id: string;
  name: string;
  percentageCompliance: number | null;
  logoUrl: string;
  location: string;
  sectorTagID: string;
  degree: number;
  criticalityTagID: string;
  confidentialityTagID: string;
  holdsPIITagID: string;
  labelIDs: string[];
  ownershipStatus: OrganisationOwnershipStatus;
};

// The `?depth=` / `?hideRiskLedgerSuppliers=` pair, passed in by the page so
// the fetch layer stays independent of the route.
export type NetworkRequestOptions = Record<string, string>;

export type ClientNetworkResponse = {
  connections: Record<string, NetworkConnection>;
  organisations: Record<string, NetworkOrganisation>;
};

type ClientNetworkConfigResponse = {
  contributionEnabled: boolean;
};

type UpdateClientNetworkConfigRequest = {
  contributionEnabled: boolean;
};

export type NetworkOrganisationForList = {
  name: string;
  id: string;
  logoUrl: string;
  country: string;
  sectorID: string;
  degree: number;
  numClients: number;
  clientNames: string[];
  numSuppliers: number;
  supplierNames: string[];
  numAnonymisedSuppliers: number;
  numVisibleSuppliers: number;
  potentialConcentrationRisk: boolean;
  critical: boolean;
};

type ClientNetworkListResponse = DatasetResponse & {
  totalOrganisations: number;
  organisations: NetworkOrganisationForList[];
};

export const fetchClientNetworkList = (payload: DatasetRequest) =>
  request<ClientNetworkListResponse>('POST', '/client-network/list', payload);

type NetworkOrganisationStatsResponse = {
  degree: number;
  numOfClients: number;
};

export const fetchOrgStats = (orgID: string) =>
  request<NetworkOrganisationStatsResponse>('GET', `/client-network/organisations/${orgID}/stats`);

export type CommunityNetworkOrganisation = Omit<NetworkOrganisation, 'ownershipStatus'> & {
  isCommunityMember: boolean;
  isSupplier: boolean;
};

export type CommunityNetworkResponse = {
  connections: Record<string, NetworkConnection>;
  organisations: Record<string, CommunityNetworkOrganisation>;
};

export function useClientNetworkConfig(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_NETWORK_CONFIG_QUERY],
      async queryFn() {
        const res = await request<ClientNetworkConfigResponse>('GET', '/client-network/v2/config');
        return res.data;
      },
    }),
  );

  return { ...query, clientNetworkConfig: query.data };
}

export function useCommunityNetwork(
  communityID: MaybeRefOrGetter<string>,
  networkOptions: MaybeRefOrGetter<NetworkRequestOptions>,
  options?: QueryOptions,
) {
  const { clientNetworkConfig } = useClientNetworkConfig({
    enabled: options && toValue(options).enabled,
  });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [COMMUNITY_NETWORK_QUERY, toRef(communityID), toRef(networkOptions)] as const,
      async queryFn({ queryKey }) {
        const res = await request<CommunityNetworkResponse>(
          'GET',
          `/community-network/${queryKey[1]}`,
          undefined,
          queryKey[2],
        );
        return res.data;
      },
      enabled: () =>
        clientNetworkConfig.value?.contributionEnabled &&
        toValue(toValue(options)?.enabled) !== false,
    }),
  );

  const concentrationRiskOrgIDs = computed(() => getConcentrationRiskOrgIDs(query.data.value));

  const nthDegree = computed(() => calculateNthDegree(query.data.value));
  const maxNthDegree = computed(() => calculateMaxNthDegree(nthDegree.value));

  const communityMemberCount = computed(() => {
    if (!query.data.value) return undefined;
    return Object.values(query.data.value.organisations).filter(
      (org) => org.isCommunityMember && org.degree !== 0,
    ).length;
  });

  return {
    ...query,
    communityNetwork: query.data,
    concentrationRiskOrgIDs,
    nthDegree,
    maxNthDegree,
    communityMemberCount,
  };
}

export function useUpdateClientNetworkConfig(
  options?: MutationOptions<ClientNetworkConfigResponse, UpdateClientNetworkConfigRequest>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(config) {
        const res = await request<ClientNetworkConfig>(
          'PATCH',
          '/client-network/v2/config',
          config,
        );
        return res.data;
      },
      async onSuccess(data) {
        await queryClient.setQueryData(
          [CLIENT_NETWORK_CONFIG_QUERY],
          (oldData: ClientNetworkConfigResponse) => ({ ...oldData, ...data }),
        );
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [COMMUNITY_NETWORK_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_NETWORK_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_NETWORK_LIST_QUERY] }),
        ]);
      },
    }),
  );
}

export function useCommunityConcentrationRiskStats(
  communityID: MaybeRefOrGetter<string>,
  orgID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [
        COMMUNITY_CONCENTRATION_RISK_STATS_QUERY,
        toRef(communityID),
        toRef(orgID),
      ] as const,
      async queryFn({ queryKey }) {
        const res = await request<NetworkOrganisationStatsResponse>(
          'GET',
          `/community-network/${queryKey[1]}/organisations/${queryKey[2]}/stats`,
        );
        return res.data;
      },
    }),
  );

  return { ...query, communityOrgStats: query.data };
}

export function useClientNetwork(
  networkOptions: MaybeRefOrGetter<NetworkRequestOptions>,
  options?: QueryOptions,
) {
  const { clientNetworkConfig } = useClientNetworkConfig({
    enabled: options && toValue(options).enabled,
  });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_NETWORK_QUERY, toRef(networkOptions)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ClientNetworkResponse>(
          'GET',
          '/client-network/v2',
          undefined,
          queryKey[1],
        );
        return res.data;
      },
      enabled: () =>
        clientNetworkConfig.value?.contributionEnabled &&
        toValue(toValue(options)?.enabled) !== false,
    }),
  );

  const concentrationRiskOrgIDs = computed(() => getConcentrationRiskOrgIDs(query.data.value));

  const nthDegree = computed(() => calculateNthDegree(query.data.value));
  const maxNthDegree = computed(() => calculateMaxNthDegree(nthDegree.value));

  const isEmpty = computed(
    () => query.data.value && !Object.keys(query.data.value.connections).length,
  );

  return {
    ...query,
    clientNetwork: query.data,
    concentrationRiskOrgIDs,
    nthDegree,
    maxNthDegree,
    isEmpty,
  };
}

export function useClientNetworkList(
  params: MaybeRefOrGetter<DatasetRequest>,
  options?: QueryOptions,
) {
  const { clientNetworkConfig } = useClientNetworkConfig({
    enabled: options && toValue(options).enabled,
  });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_NETWORK_LIST_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchClientNetworkList(queryKey[1]);
        return res.data;
      },
      enabled: () =>
        clientNetworkConfig.value?.contributionEnabled &&
        toValue(toValue(options)?.enabled) !== false,
    }),
  );

  const organisations = computed(() => query.data.value?.organisations ?? []);
  const totalOrganisations = computed(() => query.data.value?.totalOrganisations ?? 0);
  const page = computed(() => query.data.value?.page ?? null);

  return { ...query, organisations, totalOrganisations, page };
}
